import config from '../../config';

const DEFAULT_TIMEOUT_MS = 20000; // 20 seconds
const DEFAULT_RETRIES = 3;
const DEFAULT_BACKOFF_MS = 1000; // 1 second

const CACHE_CONFIG = {
  contentTypes: { revalidate: 300 }, // 5 minutes
  taxonomies: { revalidate: 300 },   // 5 minutes
  content: { revalidate: 60 },       // 1 minute
  default: { revalidate: 30 },       // 30 seconds
};

function getCacheConfig(url: string): RequestInit['next'] {
  if (url.includes('content_types') || url.includes('content-types')) {
    return CACHE_CONFIG.contentTypes;
  }
  if (url.includes('taxonomies')) {
    return CACHE_CONFIG.taxonomies;
  }
  if (url.includes('contents/')) {
    return CACHE_CONFIG.content;
  }
  return CACHE_CONFIG.default;
}

/**
 * Fetch with retry logic and exponential backoff.
 * Retries on 5xx errors AND transient client-side errors (404, 408, 429) 
 * that typically occur during rolling container deployments.
 */
async function fetchWithRetry(
  url: string,
  options: RequestInit,
  retries: number = DEFAULT_RETRIES,
  backoffMs: number = DEFAULT_BACKOFF_MS
): Promise<Response> {
  let lastResponse: Response | null = null;
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const response = await fetch(url, options);
      lastResponse = response;

      // 1. Success -> Return immediately
      if (response.ok) {
        return response;
      }

      // 2. Definitive Client Errors (e.g. 400 Bad Request, 401 Unauthorized, 403 Forbidden)
      // Do NOT retry these.
      if (
        response.status >= 400 &&
        response.status < 500 &&
        response.status !== 404 && // Transient during deployments
        response.status !== 408 && // Request Timeout
        response.status !== 429    // Rate Limited
      ) {
        return response;
      }

      // 3. Transient Error (5xx, 404, 408, 429) -> Log and back off
      console.warn(
        `[API Warning] Received HTTP ${response.status} for ${url}. Retry attempt ${attempt + 1}/${retries}`
      );
    } catch (error: any) {
      lastError = error;
      console.warn(`[API Network Error] ${error.message} for ${url}. Retry attempt ${attempt + 1}/${retries}`);
    }

    // Wait before next attempt
    const isLastAttempt = attempt === retries - 1;
    if (!isLastAttempt) {
      const delay = backoffMs * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  // If retries exhausted and we have a valid HTTP response (like a confirmed 404), return it
  if (lastResponse) {
    return lastResponse;
  }

  throw lastError || new Error(`All ${retries} retry attempts failed for ${url}`);
}

export async function get(url: string, timeoutMs: number = DEFAULT_TIMEOUT_MS) {
  // Normalize trailing/leading slashes to prevent transient routing mismatches
  const cleanUrl = url.startsWith('/') ? url.slice(1) : url;
  const fullUrl = `${config.middleware}/${cleanUrl}`;

  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error(`Request timeout after ${timeoutMs}ms for ${url}`)), timeoutMs)
  );

  try {
    const response = await Promise.race([
      fetchWithRetry(fullUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        next: getCacheConfig(cleanUrl),
      }),
      timeoutPromise,
    ]);

    // Confirmed 404 after all retries failed
    if (response.status === 404) {
      console.warn(`[API 404] Resource strictly not found: ${fullUrl}`);
      return null;
    }

    if (!response.ok) {
      // For 404s, return the response body as JSON so callers can inspect
      // data.error and return null gracefully rather than throwing.
      if (response.status === 404) {
        return await response.json().catch(() => ({ error: true, message: 'Not found' }));
      }
      throw new Error(`Fetch failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error: any) {
    console.error(`[API Error] GET request failed for ${url}:`, error.message);
    throw error;
  }
}

export async function post(url: string, body?: any, timeoutMs: number = DEFAULT_TIMEOUT_MS) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const cleanUrl = url.startsWith('/') ? url.slice(1) : url;
  const fullUrl = `${config.middleware}/${cleanUrl}`;

  try {
    const response = await fetchWithRetry(
      fullUrl,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        signal: controller.signal,
        cache: 'no-store',
      }
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`Fetch failed with status ${response.status}`);
    }

    return await response.json();
  } catch (error: any) {
    clearTimeout(timeoutId);

    if (error.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeoutMs}ms for ${url}`);
    }

    throw error;
  }
}
